import React, { useState } from "react";
import { Layout, Menu, Row } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  AccountBookOutlined,
} from "@ant-design/icons";
import LayoutHeader from "./header";
import LayoutFooter from "./footer";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/slices/authSlice";
import { useMatchedRoute } from "../../routers";
import IdleSessionTimeout from "./IdleSessionTimeout";
import BreadcrumbItem from "../../components/breadcrumb";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import PermUtils from "../../utils/perm-utils";
import { DETAIL_PACKAGE, HISTORY, ORDER, OVERVIEW, ROOT_ACCOUNT } from "../../assets/constants/route_path";
import { getDefaultAuthenticatedRoute } from "../../utils/app-utils";
import logoImg from '../../assets/images/logo.png';
import { APP_VERSION } from "../../config/constants";

const { Sider, Content } = Layout;

type MenuItem = {
  key: any;
  label: string | React.ReactNode;
  link?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
};

const buildMenuItem = ({
  key,
  label,
  link,
  icon,
  children,
  allowedfunctionids,
}: {
  label: string | React.ReactNode;
  key: string;
  link: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
}): MenuItem => {
  const item = {
    key,
    label,
    link,
    icon,
    children,
    allowedfunctionids,
  } as MenuItem;
  const hasChildren = children && children.length > 0;

  // Leaf item
  if (link && !hasChildren) {
    item.label = <Link to={link}>{label}</Link>;
  }

  return item;
};

const allItems: MenuItem[] = [
  buildMenuItem({
    key: "RootAccount",
    label: "Đăng ký gói cước",
    link: ROOT_ACCOUNT,
    icon: <AccountBookOutlined />,
    allowedfunctionids: [],
    children: [
      {
        key: ROOT_ACCOUNT + OVERVIEW,
        label: <Link to={ROOT_ACCOUNT + OVERVIEW}>Tổng quan</Link>,
        link: ROOT_ACCOUNT + OVERVIEW,
        allowedfunctionids: [PermUtils.canCheckBalanceTkg],
      },
      {
        key: ROOT_ACCOUNT + ORDER,
        label: <Link to={ROOT_ACCOUNT + ORDER}>Đăng ký</Link>,
        link: ROOT_ACCOUNT + ORDER,
        allowedfunctionids: [PermUtils.canGetListOrderTkgDashboard],
      },
      {
        key: ROOT_ACCOUNT + HISTORY,
        label: <Link to={ROOT_ACCOUNT + HISTORY}>Lịch sử giao dịch</Link>,
        link: ROOT_ACCOUNT + HISTORY,
        allowedfunctionids: [PermUtils.canGetListTransactionTkgDashboardByOrderId],
      },
      {
        key: ROOT_ACCOUNT + DETAIL_PACKAGE,
        label: <Link to={ROOT_ACCOUNT + DETAIL_PACKAGE}>Danh sách gói cước</Link>,
        link: ROOT_ACCOUNT + DETAIL_PACKAGE,
        allowedfunctionids: [PermUtils.canGetListPackagesTkgDashboard],
      },
    ],
  }),
];

const isAuthorizedItem = (item: MenuItem, userFnIDs: string[]): boolean => {
  if (item?.allowedfunctionids && item?.allowedfunctionids?.length > 0) {
    return item?.allowedfunctionids?.some((id) => userFnIDs?.includes(id));
  }

  return true;
};

const authorizeMenuItems = (
  items: MenuItem[] = [],
  userFnIDs: string[] = [],
): MenuItem[] => {
  // Filter active items for origin array
  items = items?.filter((item) => isAuthorizedItem(item, userFnIDs));

  // Filter active items for nested items as children
  items?.forEach((item) => {
    if (item?.children && item?.children?.length > 0) {
      item.children = authorizeMenuItems(item.children, userFnIDs);
    }
  });

  return items;
};

const listAllActiveItems = (
  items: MenuItem[],
  currentPath: string | undefined,
): MenuItem[] => {
  const activeItems = items.filter(
    (item) => item.link && currentPath?.startsWith(item.link),
  );

  const childActiveItems = activeItems
    .map((item) => {
      if (item.children && item.children.length > 0) {
        return listAllActiveItems(item.children, currentPath);
      }

      return [];
    })
    .flat();

  return [...activeItems, ...childActiveItems];
};

const getLevelKeys = (items1: MenuItem[]) => {
  const key: Record<string, number> = {};
  const func = (items2: any, level = 1) => {
    items2.forEach((item: any) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

const MainLayout = ({ children }: any) => {
  const authState = useSelector(authSelector);
  const { account } = authState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const currentRoutePath = useMatchedRoute();
  const [collapsed, setCollapsed] = useState(false);
  console.log("account", account)
  // Avoid execute these functions multiple times
  const authorizedItems = authorizeMenuItems(allItems, account.listPermission);

  // Always expand all items
  const openedKeys = authorizedItems.map((item) => item.key);

  const activeItems = listAllActiveItems(authorizedItems, currentRoutePath);

  const activeItemLength = activeItems.length;
  const activeItemKeys = activeItems.map((item) => item.key);
  const selectedKey =
    activeItemLength === 0 ? null : activeItemKeys[activeItemLength - 1];

  const levelKeys = getLevelKeys(authorizedItems);
  const [stateOpenKeys, setStateOpenKeys] = useState(['RootAccount']);

  const getSiderFooter = () => {
    return (
      <Row
        justify='space-between'
        align='middle'
        style={{ padding: "0 1rem " }}>
        <div style={{ fontSize: 14 }}>
          <SettingOutlined /> Phiên bản {APP_VERSION}
        </div>
        <div>
          <LeftOutlined />
        </div>
      </Row>
    );
  };

  const onOpenChange = (openKeys: any) => {
    const currentOpenKey = openKeys.find((key: any) => stateOpenKeys.indexOf(key) === -1);
    // open
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key: any) => key !== currentOpenKey)
        .findIndex((key: any) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          // remove repeat key
          .filter((_: any, index: any) => index !== repeatIndex)
          // remove current level all child
          .filter((key: any) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  };

  return (
    <Layout hasSider>
      <Sider
        breakpoint='md'
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
        }}
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        trigger={!collapsed ? getSiderFooter() : <RightOutlined />}>
        <Row
          justify='center'
          style={{ backgroundColor: "white", cursor: "pointer" }}
          onClick={() => navigate(getDefaultAuthenticatedRoute(account))}
        >
          {collapsed ? (
            <img
              src={logoImg}
              alt='Otek'
              style={{
                width: 65,
                height: 65,
                objectFit: "cover",
                objectPosition: "left",
                transform: "scale(0.8)",
              }}
            />
          ) : (
            <img
              src={logoImg}
              alt='Otek'
              style={{
                height: 65,
                transform: "scale(0.8)",
              }}
            />
          )}
        </Row>
        <Menu
          theme='dark'
          mode='inline'
          defaultOpenKeys={openedKeys}
          items={authorizedItems}
          selectedKeys={selectedKey}
          openKeys={stateOpenKeys}
          onOpenChange={onOpenChange}
        />
      </Sider>
      <Layout
        className='site-layout'
        style={{
          marginLeft: collapsed ? 80 : 250,
          transition: "margin-left 0.2s",
        }}>
        <LayoutHeader profile={account} collapsed={collapsed} />
        <div
          style={{
            padding: 12,
            marginTop: 64,
            paddingBottom: 0,
          }}>
          <BreadcrumbItem />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "calc(100vh - 100px)",
          }}>
          <Content
            style={{
              padding: 12,
              flexGrow: 1,
            }}>
            <div
              style={{
                minHeight: "100%",
                backgroundColor: "white",
              }}>
              {children}
            </div>
          </Content>
          <LayoutFooter />
        </div>
      </Layout>
      <IdleSessionTimeout account={authState.account} />
    </Layout>
  );
};

export default MainLayout;
